import { GlModal, GlSearchBoxByType, GlButton, GlIcon, GlTooltipDirective, GlLoadingIcon, GlLink, GlAlert } from '@gitlab/ui';
import { __, s__, sprintf } from '~/locale';
import { ROUTES } from '~/work_items/constants';
import { subscribeWithLimitEnforce } from 'ee_else_ce/work_items/list/utils';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { SAVED_VIEW_SORT_NAME_ASC, BROWSE_SAVED_VIEWS_PAGE_SIZE } from '~/work_items/list/constants';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { helpPagePath } from '~/helpers/help_page_helper';
import getNamespaceSavedViewsQuery from '../graphql/work_item_saved_views_namespace.query.graphql';
export default {
    name: 'WorkItemsExistingSavedViewsModal',
    components: {
        GlModal,
        GlSearchBoxByType,
        GlButton,
        GlIcon,
        GlLoadingIcon,
        GlLink,
        GlAlert
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    i18n: {
        title: s__('WorkItem|Views'),
        searchPlaceholder: s__('WorkItem|Search by view name or description'),
        emptyViews: s__('WorkItem|No views yet'),
        emptyViewsDescription: s__('WorkItem|Views created by you or shared by others will show up here.'),
        noPermissionAlert: s__("WorkItem|You don't have permission to create views in this %{namespaceType}, but you can use views members of this %{namespaceType} have shared with you."),
        noPermissionEmptyDescription: s__("WorkItem|You don't have permission to create views in this %{namespaceType}. Views shared by members of this %{namespaceType} will appear here."),
        notFound: s__('WorkItem|No results found'),
        notFoundDescription: s__('WorkItem|Edit your search and try again.'),
        privateTooltip: s__('WorkItem|Private: only you can see and edit this view.'),
        subscriptionLimitWarningMessage: s__('WorkItem|You have reached the maximum number of views in your list. If you add a view, the last view in your list will be removed.'),
        learnMoreAboutViewLimits: s__('WorkItem|Learn more about view limits.')
    },
    savedViewLimitsHelpPath: helpPagePath('user/work_items/saved_views.md', {
        anchor: 'saved-view-limits'
    }),
    DEFAULT_DEBOUNCE_AND_THROTTLE_MS,
    inject: [
        'canCreateSavedView',
        'subscribedSavedViewLimit',
        'isGroup'
    ],
    model: {
        prop: 'show',
        event: 'hide'
    },
    props: {
        show: {
            type: Boolean,
            required: true
        },
        fullPath: {
            type: String,
            required: true
        },
        showSubscriptionLimitWarning: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    // TODO: Add 'view-subscribed' emit once subscribe functionality is implemented
    // See: https://gitlab.com/gitlab-org/gitlab/-/work_items/588295
    emits: [
        'hide',
        'show-new-view-modal',
        'subscribe-from-modal'
    ],
    data () {
        return {
            searchInput: '',
            savedViewsConnection: null,
            isLoadingMore: false,
            error: undefined
        };
    },
    apollo: {
        savedViewsConnection: {
            query: getNamespaceSavedViewsQuery,
            variables () {
                return {
                    fullPath: this.fullPath,
                    subscribedOnly: false,
                    sort: SAVED_VIEW_SORT_NAME_ASC,
                    first: BROWSE_SAVED_VIEWS_PAGE_SIZE,
                    search: this.searchInput || undefined
                };
            },
            skip () {
                return !this.show;
            },
            update (data) {
                return data.namespace?.savedViews ?? null;
            },
            error (e) {
                Sentry.captureException(e);
            }
        }
    },
    computed: {
        isLoading () {
            return this.$apollo.queries.savedViewsConnection.loading;
        },
        savedViews () {
            return this.savedViewsConnection?.nodes ?? [];
        },
        namespaceType () {
            return this.isGroup ? __('group') : __('project');
        },
        noPermissionAlertMessage () {
            return sprintf(this.$options.i18n.noPermissionAlert, {
                namespaceType: this.namespaceType
            });
        },
        emptyStateDescription () {
            if (!this.canCreateSavedView) {
                return sprintf(this.$options.i18n.noPermissionEmptyDescription, {
                    namespaceType: this.namespaceType
                });
            }
            return this.$options.i18n.emptyViewsDescription;
        },
        hasSavedViews () {
            return this.savedViews.length > 0;
        },
        hasSearchInput () {
            return this.searchInput.trim().length > 0;
        },
        pageInfo () {
            return this.savedViewsConnection?.pageInfo;
        },
        hasNextPage () {
            return this.pageInfo?.hasNextPage ?? false;
        },
        endCursor () {
            return this.pageInfo?.endCursor ?? null;
        },
        isInitialLoading () {
            return this.isLoading && !this.isLoadingMore;
        },
        shouldShowEmptyState () {
            return !this.isLoading && !this.hasSavedViews && !this.hasSearchInput;
        },
        shouldShowNoSearchResults () {
            return this.hasSearchInput && !this.hasSavedViews;
        },
        shouldShowSearchBox () {
            if (!this.canCreateSavedView) {
                return this.hasSavedViews || this.hasSearchInput;
            }
            return this.hasSavedViews || this.hasSearchInput;
        },
        shouldShowNoPermissionAlert () {
            if (this.hasSearchInput) {
                return !this.canCreateSavedView;
            }
            return !this.canCreateSavedView && this.hasSavedViews;
        }
    },
    methods: {
        focusSearchInput () {
            this.$refs.savedViewSearch?.$el.focus();
        },
        hideModal () {
            this.$emit('hide', false);
            this.searchInput = '';
        },
        redirectToNewViewModal () {
            this.$emit('hide', false);
            this.$emit('show-new-view-modal');
        },
        async fetchMoreViews () {
            if (this.isLoadingMore || !this.hasNextPage) return;
            this.isLoadingMore = true;
            try {
                await this.$apollo.queries.savedViewsConnection.fetchMore({
                    variables: {
                        after: this.endCursor
                    }
                });
            } catch (e) {
                Sentry.captureException(e);
            } finally{
                this.isLoadingMore = false;
            }
        },
        async redirectToView (view) {
            const viewId = getIdFromGraphQLId(view.id).toString();
            if (!view.subscribed) {
                this.$emit('subscribe-from-modal');
                const mutationKey = 'workItemSavedViewSubscribe';
                try {
                    const { data } = await subscribeWithLimitEnforce({
                        view,
                        apolloClient: this.$apollo,
                        namespacePath: this.fullPath,
                        subscribedSavedViewLimit: this.subscribedSavedViewLimit
                    });
                    if (data[mutationKey].errors?.length) {
                        this.error = s__('WorkItem|An error occurred while subscribing to the view. Please try again.');
                        return;
                    }
                    this.$router.push({
                        name: ROUTES.savedView,
                        params: {
                            view_id: viewId
                        },
                        query: undefined
                    });
                } catch (e) {
                    this.error = s__('WorkItem|An error occurred while subscribing to the view. Please try again.');
                    return;
                }
            } else {
                this.$router.push({
                    name: ROUTES.savedView,
                    params: {
                        view_id: viewId
                    },
                    query: undefined
                });
            }
            this.hideModal();
        }
    }
};
